import React, { useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Download,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';

const CertificateManager = () => {
  const [certificates, setCertificates] = useState([
    {
      id: 'CO2N-2024-001',
      companyName: 'TechCorp GmbH',
      website: 'techcorp.de',
      status: 'active',
      issuedDate: '2024-01-15',
      expiryDate: '2025-01-15',
      carbonOffset: '2.5 tons',
      verified: true
    },
    {
      id: 'CO2N-2024-002',
      companyName: 'GreenStart Solutions',
      website: 'greenstart.com',
      status: 'active',
      issuedDate: '2024-01-20',
      expiryDate: '2025-01-20',
      carbonOffset: '1.8 tons',
      verified: true
    },
    {
      id: 'CO2N-2024-003',
      companyName: 'EcoTech Industries',
      website: 'ecotech.com',
      status: 'pending',
      issuedDate: '2024-01-25',
      expiryDate: '2025-01-25',
      carbonOffset: '3.2 tons',
      verified: false
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [newCertificate, setNewCertificate] = useState({
    companyName: '',
    website: '',
    carbonOffset: '',
    issuedDate: '',
    expiryDate: ''
  });

  const handleAddCertificate = () => {
    const certificate = {
      id: `CO2N-2024-${String(certificates.length + 1).padStart(3, '0')}`,
      ...newCertificate,
      status: 'active',
      verified: true
    };
    setCertificates([...certificates, certificate]);
    setShowAddModal(false);
    setNewCertificate({
      companyName: '',
      website: '',
      carbonOffset: '',
      issuedDate: '',
      expiryDate: ''
    });
  };

  const handleEditCertificate = (certificate) => {
    setEditingCertificate(certificate);
  };

  const handleUpdateCertificate = () => {
    setCertificates(certificates.map(cert => 
      cert.id === editingCertificate.id ? editingCertificate : cert
    ));
    setEditingCertificate(null);
  };

  const handleDeleteCertificate = (id) => {
    if (window.confirm('Bu sertifikayı silmek istediğinizden emin misiniz?')) {
      setCertificates(certificates.filter(cert => cert.id !== id));
    }
  };

  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = cert.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cert.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Aktif
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Beklemede
        </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Süresi Dolmuş
        </span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-neutral-800">Sertifika Yönetimi</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Yeni Sertifika</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-neutral-200">
        <div className="flex space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Şirket adı veya sertifika ID'si ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="active">Aktif</option>
            <option value="pending">Beklemede</option>
            <option value="expired">Süresi Dolmuş</option>
          </select>
        </div>
      </div>

      {/* Certificates Table */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Sertifika ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Şirket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Karbon Dengeleme
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Geçerlilik
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {filteredCertificates.map((certificate) => (
                <tr key={certificate.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                    {certificate.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-neutral-900">
                        {certificate.companyName}
                      </div>
                      <div className="text-sm text-neutral-500">
                        {certificate.website}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(certificate.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                    {certificate.carbonOffset}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                    {new Date(certificate.expiryDate).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditCertificate(certificate)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCertificate(certificate.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button className="text-neutral-600 hover:text-neutral-900">
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Certificate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">Yeni Sertifika Ekle</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Şirket Adı
                </label>
                <input
                  type="text"
                  value={newCertificate.companyName}
                  onChange={(e) => setNewCertificate({...newCertificate, companyName: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Web Sitesi
                </label>
                <input
                  type="text"
                  value={newCertificate.website}
                  onChange={(e) => setNewCertificate({...newCertificate, website: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Karbon Dengeleme
                </label>
                <input
                  type="text"
                  value={newCertificate.carbonOffset}
                  onChange={(e) => setNewCertificate({...newCertificate, carbonOffset: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Veriliş Tarihi
                  </label>
                  <input
                    type="date"
                    value={newCertificate.issuedDate}
                    onChange={(e) => setNewCertificate({...newCertificate, issuedDate: e.target.value})}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Geçerlilik Tarihi
                  </label>
                  <input
                    type="date"
                    value={newCertificate.expiryDate}
                    onChange={(e) => setNewCertificate({...newCertificate, expiryDate: e.target.value})}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleAddCertificate}
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Ekle
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 py-2 px-4 rounded-lg transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Certificate Modal */}
      {editingCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">Sertifika Düzenle</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Şirket Adı
                </label>
                <input
                  type="text"
                  value={editingCertificate.companyName}
                  onChange={(e) => setEditingCertificate({...editingCertificate, companyName: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Durum
                </label>
                <select
                  value={editingCertificate.status}
                  onChange={(e) => setEditingCertificate({...editingCertificate, status: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="active">Aktif</option>
                  <option value="pending">Beklemede</option>
                  <option value="expired">Süresi Dolmuş</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleUpdateCertificate}
                className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Güncelle
              </button>
              <button
                onClick={() => setEditingCertificate(null)}
                className="flex-1 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 py-2 px-4 rounded-lg transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateManager; 